import { Form, Link } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { MasterclassCoverImageField } from "@/components/masterclasses/masterclass-cover-image-field"
import { MasterclassStatusBadge } from "@/components/masterclasses/masterclass-status-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { masterclassPath, masterclassesPath } from "@/routes"
import type { MasterclassRow } from "@/types/masterclasses"

const TITLE_MAX = 120
const TOPIC_MAX = 60
const DESCRIPTION_MAX = 2_000

/**
 * OKS-54 — Shared `<MasterclassForm>` body for `New` and `Edit` pages.
 *
 * **Crew hard rule (P0):** uses Inertia `<Form>` from `@inertiajs/react`
 * with plain shadcn primitives + `name` attributes. NO shadcn `Form`,
 * NO `react-hook-form`, NO `zod`. Errors render manually from the
 * `errors` render-prop (UX Spec §3 Form/Error).
 *
 * Layout (UX Spec §2.3):
 *   - Desktop ≥1024px: two-column — form left 2/3, summary card right 1/3
 *   - Tablet / mobile <1024px: single column — summary moves below
 *
 * Submit footer:
 *   - Cancel (link back to Show on Edit, Index on New)
 *   - Save draft (always; on Edit preserves draft state)
 *   - Save & publish (always on New; on Edit only when state=draft —
 *     `can_publish_intent` gate)
 *
 * `commit` discriminator (TRD KD-10):
 *   - `commit="draft"` → controller persists as draft (or leaves state
 *     untouched on update)
 *   - `commit="publish"` → controller calls `publish!` after save
 *     (or after update). The state column is NEVER permitted from form
 *     params — `commit` is the only mutator.
 */
export interface MasterclassFormProps {
  masterclass: MasterclassRow
  /**
   * `"new"` posts to `masterclassesPath()`; `"edit"` patches
   * `masterclassPath(id)`. The form wrapper switches verb + URL based
   * on this discriminator so the field set stays symmetric.
   */
  mode: "new" | "edit"
}

export function MasterclassForm({ masterclass, mode }: MasterclassFormProps) {
  const { t } = useTranslation()

  const [title, setTitle] = useState(masterclass.title ?? "")
  const [topic, setTopic] = useState(masterclass.topic ?? "")
  const [description, setDescription] = useState(masterclass.description ?? "")

  const action =
    mode === "new" ? masterclassesPath() : masterclassPath(masterclass.id ?? 0)
  const method = mode === "new" ? "post" : "patch"

  // "Save & publish" is shown on `new` always, and on `edit` only when
  // the row is currently a draft. An already-published edit doesn't
  // need the publish-intent button — the row stays published on save
  // and the dedicated Publish/Unpublish actions live on Show.
  const canPublishIntent = mode === "new" || masterclass.state === "draft"

  const cancelHref =
    mode === "edit" && masterclass.id
      ? masterclassPath(masterclass.id)
      : masterclassesPath()

  return (
    <Form
      action={action}
      method={method}
      className="grid gap-6 lg:grid-cols-3"
      data-testid={`masterclass-form-${mode}`}
    >
      {({ processing, errors }) => (
        <>
          {/* ---------- Form column ---------- */}
          <div className="flex flex-col gap-6 lg:col-span-2">
            {/* Cover image (OKS-649) — banner field above Title */}
            <MasterclassCoverImageField
              currentUrl={masterclass.cover_image_url ?? null}
              hasCoverImage={masterclass.has_cover_image ?? false}
              masterclassId={masterclass.id}
              error={errors.cover_image}
            />

            {/* Title */}
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="masterclass_title">
                  {t("masterclasses.form.fields.title.label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <span
                  className="text-muted-foreground text-xs tabular-nums"
                  aria-hidden="true"
                >
                  {t("masterclasses.form.counter", {
                    count: title.length,
                    max: TITLE_MAX,
                  })}
                </span>
              </div>
              <Input
                id="masterclass_title"
                name="masterclass[title]"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={TITLE_MAX}
                placeholder={t("masterclasses.form.fields.title.placeholder")}
                aria-invalid={errors.title ? "true" : undefined}
                aria-describedby={
                  errors.title ? "masterclass_title_error" : undefined
                }
                data-testid="masterclass-form-title"
                aria-required="true"
              />
              {errors.title && (
                <p
                  id="masterclass_title_error"
                  className="text-destructive text-sm"
                  role="alert"
                  data-testid="masterclass-form-title-error"
                >
                  {errors.title}
                </p>
              )}
            </div>

            {/* Topic */}
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="masterclass_topic">
                  {t("masterclasses.form.fields.topic.label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <span
                  className="text-muted-foreground text-xs tabular-nums"
                  aria-hidden="true"
                >
                  {t("masterclasses.form.counter", {
                    count: topic.length,
                    max: TOPIC_MAX,
                  })}
                </span>
              </div>
              <Input
                id="masterclass_topic"
                name="masterclass[topic]"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                maxLength={TOPIC_MAX}
                placeholder={t("masterclasses.form.fields.topic.placeholder")}
                aria-invalid={errors.topic ? "true" : undefined}
                aria-describedby={
                  errors.topic
                    ? "masterclass_topic_error"
                    : "masterclass_topic_helper"
                }
                data-testid="masterclass-form-topic"
                aria-required="true"
              />
              <p
                id="masterclass_topic_helper"
                className="text-muted-foreground text-xs"
              >
                {t("masterclasses.form.fields.topic.helper")}
              </p>
              {errors.topic && (
                <p
                  id="masterclass_topic_error"
                  className="text-destructive text-sm"
                  role="alert"
                  data-testid="masterclass-form-topic-error"
                >
                  {errors.topic}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="masterclass_description">
                  {t("masterclasses.form.fields.description.label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <span
                  className="text-muted-foreground text-xs tabular-nums"
                  aria-hidden="true"
                >
                  {t("masterclasses.form.counter", {
                    count: description.length,
                    max: DESCRIPTION_MAX,
                  })}
                </span>
              </div>
              <Textarea
                id="masterclass_description"
                name="masterclass[description]"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={DESCRIPTION_MAX}
                placeholder={t(
                  "masterclasses.form.fields.description.placeholder",
                )}
                rows={8}
                aria-invalid={errors.description ? "true" : undefined}
                aria-describedby={
                  errors.description
                    ? "masterclass_description_error"
                    : undefined
                }
                data-testid="masterclass-form-description"
                aria-required="true"
              />
              {errors.description && (
                <p
                  id="masterclass_description_error"
                  className="text-destructive text-sm"
                  role="alert"
                  data-testid="masterclass-form-description-error"
                >
                  {errors.description}
                </p>
              )}
            </div>

            {/* Footer button row */}
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button asChild variant="outline" type="button">
                <Link href={cancelHref} data-testid="masterclass-form-cancel">
                  {t("masterclasses.form.actions.cancel")}
                </Link>
              </Button>
              <Button
                type="submit"
                name="commit"
                value="draft"
                variant="secondary"
                disabled={processing}
                data-testid="masterclass-form-save-draft"
              >
                {processing && (
                  <Loader2
                    className="mr-2 size-4 animate-spin"
                    aria-hidden="true"
                  />
                )}
                {mode === "new"
                  ? t("masterclasses.form.actions.save_draft")
                  : t("masterclasses.form.actions.save_changes")}
              </Button>
              {canPublishIntent && (
                <Button
                  type="submit"
                  name="commit"
                  value="publish"
                  disabled={processing}
                  data-testid="masterclass-form-save-and-publish"
                >
                  {processing && (
                    <Loader2
                      className="mr-2 size-4 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  {t("masterclasses.form.actions.save_and_publish")}
                </Button>
              )}
            </div>
          </div>

          {/* ---------- Summary right-rail (UX Spec §2.3) ---------- */}
          <Card
            className="h-fit lg:col-span-1"
            data-testid="masterclass-form-summary"
          >
            <CardHeader>
              <CardTitle>{t("masterclasses.form.summary_heading")}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <p className="text-muted-foreground text-xs uppercase">
                  {t("masterclasses.form.fields.title.label")}
                </p>
                <p className="text-sm font-medium">
                  {title || (
                    <span className="text-muted-foreground italic">—</span>
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-muted-foreground text-xs uppercase">
                  {t("masterclasses.form.fields.topic.label")}
                </p>
                <p className="text-sm">
                  {topic || (
                    <span className="text-muted-foreground italic">—</span>
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-muted-foreground text-xs uppercase">
                  {t("masterclasses.form.fields.description.label")}
                </p>
                <p className="text-muted-foreground line-clamp-4 text-sm">
                  {description || <span className="italic">—</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 border-t pt-3">
                <p className="text-muted-foreground text-xs uppercase">
                  {t("masterclasses.form.summary_status_label")}
                </p>
                <MasterclassStatusBadge state={masterclass.state} />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </Form>
  )
}
